import { FormHelperText, TextField } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import { makeStyles } from "@material-ui/core/styles";
import ConfirmButton, {
  ConfirmButtonTransitionState
} from "@saleor/components/ConfirmButton";
import Form from "@saleor/components/Form";
import FormSpacer from "@saleor/components/FormSpacer";
import SingleSelectField from "@saleor/components/SingleSelectField";
import { ServerErrorFragment } from "@saleor/fragments/types/ServerErrorFragment";
import { buttonMessages } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import { SettlementStatusEnum } from "@saleor/types/globalTypes";
import { getFormErrors } from "@saleor/utils/errors";
import getPayoutMessage from "@saleor/utils/errors/payout";
import { dateTimePhTimeZone } from "@saleor/utils/smop/timeZonePH";
import {
  hasNoError,
  payoutSettlementValidation
} from "@saleor/utils/validators/payoutSettlement";
import Moment from "moment";
import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { PayoutStoreOrderDetails_payoutStoreOrder } from "../../types/PayoutStoreOrderDetails";

export interface PayoutSettlementDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: any;
  confirmButtonState: ConfirmButtonTransitionState;
  errors: ServerErrorFragment[];
  FinanceStoreOrderDetails: PayoutStoreOrderDetails_payoutStoreOrder;
}

export interface PayoutSettlementFormData {
  settlementDate: string;
  settlementStatus: string;
  paymentFeeAdjustment: number;
  paymentFeeAdjustmentVat: number;
  otherAdjustment: number;
}

export interface PayoutSettlementFormValidation {
  settlementDate: string;
  settlementStatus: string;
  paymentFeeAdjustment: number;
  paymentFeeAdjustmentVat: number;
  otherAdjustment: number;
}

const useStyles = makeStyles(
  () => ({
    buttonActionFlex: {
      flexDirection: "column"
    },
    buttonActions: {
      display: "flex",
      width: "100%"
    },
    buttonWidth: {
      margin: "3px",
      width: "100%"
    },
    errorText: {
      color: "#EC5D77",
      fontSize: "0.75rem",
      margin: "6px 14px"
    },
    singleSelectField: {
      "& label": {
        overflowX: "visible",
        width: "100%"
      }
    },
    textBold: {
      "&:nth-child(1)": {
        marginTop: "0px"
      },
      fontSize: "14px",
      fontWeight: 600,
      margin: "12px 0px"
    }
  }),
  { name: "PayoutSettlementDialog" }
);

const PayoutSettlementDialog: React.SFC<PayoutSettlementDialogProps> = props => {
  const {
    onClose,
    open,
    onSubmit,
    FinanceStoreOrderDetails,
    confirmButtonState,
    errors
  } = props;

  const classes = useStyles(props);
  const intl = useIntl();
  const container = React.useRef<HTMLDivElement>();
  const disabled = FinanceStoreOrderDetails ? false : true;

  const initialForm: PayoutSettlementFormData = {
    otherAdjustment: maybe(() => FinanceStoreOrderDetails.other_adjustment, 0),
    paymentFeeAdjustment: maybe(
      () => FinanceStoreOrderDetails.payment_fee_adjustment,
      0
    ),
    paymentFeeAdjustmentVat: maybe(() => null, 0),
    settlementDate: dateTimePhTimeZone(
      maybe(() => FinanceStoreOrderDetails.settlement_date, null)
    ).date,
    settlementStatus: maybe(() => FinanceStoreOrderDetails.settlement_status)
  };
  const settlementStatus = [
    { label: "For Payment", value: SettlementStatusEnum.FOR_PAYMENT },
    { label: "Exclude", value: SettlementStatusEnum.EXCLUDED }
  ];

  const formFields = [
    "settlementStatus",
    "settlementDate",
    "paymentFeeAdjustment",
    "paymentFeeAdjustmentVat",
    "otherAdjustment"
  ];

  const formErrors = getFormErrors(formFields, errors);

  const initalFormValidation: PayoutSettlementFormValidation = {
    otherAdjustment: null,
    paymentFeeAdjustment: null,
    paymentFeeAdjustmentVat: null,
    settlementDate: null,
    settlementStatus: null
  };
  const [clientError, setErrorValdiation] = useState(initalFormValidation);

  const handleSubmit = (event: any) => {
    const validate = payoutSettlementValidation(event);
    if (hasNoError(validate)) {
      setErrorValdiation(validate);
      onSubmit(event);
    } else {
      setErrorValdiation(validate);
    }
  };
  const payoutDate = Moment.tz(
    new Date(FinanceStoreOrderDetails?.payout_date),
    "Asia/Manila"
  )
    .add(2, "days")
    .format("YYYY-MM-DD");

  return (
    <Form initial={initialForm} onSubmit={handleSubmit} confirmLeave>
      {({ data: formData, change, submit }) => (
        <Dialog onClose={onClose} open={open} maxWidth="xs" fullWidth>
          <>
            <DialogContent ref={container}>
              <FormSpacer />
              <SingleSelectField
                choices={settlementStatus}
                disabled={disabled}
                label={intl.formatMessage({
                  defaultMessage: "Settlement Status"
                })}
                name="settlementStatus"
                onChange={change}
                value={formData.settlementStatus}
              />
              {formErrors?.settlementStatus ? (
                <FormHelperText className={classes.errorText}>
                  {getPayoutMessage(formErrors.settlementStatus, intl)}
                </FormHelperText>
              ) : clientError?.settlementStatus ? (
                <FormHelperText className={classes.errorText}>
                  {clientError?.settlementStatus}
                </FormHelperText>
              ) : null}
              <FormSpacer />

              <TextField
                className={classes.singleSelectField}
                label={intl.formatMessage({
                  defaultMessage: "Settlement Date",
                  description: "select settlement date"
                })}
                disabled={disabled}
                type="date"
                name="settlementDate"
                onChange={change}
                value={formData.settlementDate}
                helperText={
                  getPayoutMessage(formErrors.settlementDate, intl) ||
                  clientError?.settlementDate
                }
                error={
                  !!formErrors.settlementDate || !!clientError?.settlementDate
                }
                fullWidth
                inputProps={{
                  max: Moment(payoutDate)
                    .add(7, "days")
                    .format("YYYY-MM-DD"),
                  min: payoutDate
                }}
                InputLabelProps={{
                  shrink: true
                }}
              />
              <FormSpacer />

              <TextField
                className={classes.singleSelectField}
                label={intl.formatMessage({
                  defaultMessage: "Adjustment on Payment Fee",
                  description: "input Adjustment on Payment Fee"
                })}
                disabled={disabled}
                type="number"
                name="paymentFeeAdjustment"
                onChange={change}
                value={formData.paymentFeeAdjustment}
                helperText={
                  getPayoutMessage(formErrors.paymentFeeAdjustment, intl) ||
                  clientError?.paymentFeeAdjustment
                }
                error={
                  !!formErrors.paymentFeeAdjustment ||
                  !!clientError?.paymentFeeAdjustment
                }
                fullWidth
                InputLabelProps={{
                  shrink: true
                }}
              />
              <FormSpacer />

              <TextField
                className={classes.singleSelectField}
                label={intl.formatMessage({
                  defaultMessage: "Adjustment VAT on Payment Fee",
                  description: "input adjustment vAT on payment fee"
                })}
                disabled={disabled}
                type="number"
                name="paymentFeeAdjustmentVat"
                onChange={change}
                value={formData.paymentFeeAdjustmentVat}
                helperText={
                  getPayoutMessage(formErrors.paymentFeeAdjustmentVat, intl) ||
                  clientError?.paymentFeeAdjustmentVat
                }
                error={
                  !!formErrors.paymentFeeAdjustmentVat ||
                  !!clientError?.paymentFeeAdjustmentVat
                }
                fullWidth
                InputLabelProps={{
                  shrink: true
                }}
              />
              <FormSpacer />

              <TextField
                className={classes.singleSelectField}
                label={intl.formatMessage({
                  defaultMessage: "Other Adjustment",
                  description: "input other adjustment"
                })}
                disabled={disabled}
                type="number"
                name="otherAdjustment"
                onChange={change}
                value={formData.otherAdjustment}
                helperText={
                  getPayoutMessage(formErrors.otherAdjustment, intl) ||
                  clientError?.otherAdjustment
                }
                error={
                  !!formErrors.otherAdjustment || !!clientError?.otherAdjustment
                }
                fullWidth
                InputLabelProps={{
                  shrink: true
                }}
              />
            </DialogContent>
            <DialogActions className={classes.buttonActionFlex}>
              <div className={classes.buttonActions}>
                <Button onClick={onClose} className={classes.buttonWidth}>
                  <FormattedMessage {...buttonMessages.back} />
                </Button>
                <ConfirmButton
                  transitionState={confirmButtonState}
                  color="primary"
                  variant="contained"
                  type="submit"
                  onClick={submit}
                  className={classes.buttonWidth}
                >
                  <FormattedMessage
                    defaultMessage="SAVE"
                    description="button"
                  />
                </ConfirmButton>
              </div>
            </DialogActions>
          </>
        </Dialog>
      )}
    </Form>
  );
};
PayoutSettlementDialog.displayName = "PayoutSettlementDialog";
export default PayoutSettlementDialog;
